import React, { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { MapPin, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Slice C1 · Pin-drag map for fine-tuning pickup / drop-off.
 *
 * Renders a tiny MapLibre GL map with a fixed centre pin. As the user
 * pans the map, the map centre becomes the new coordinate and we
 * reverse-geocode it to fill the address field. Cheap and physical —
 * exactly the Grab/Uber pickup UX.
 *
 * Props
 *  · initial    {lat,lng,label?}
 *  · onCommit   ({lat,lng,label}) → parent's setPickup / setDropoff
 *  · label      "Pickup" | "Dropoff"          (for the header pill)
 *  · height     "180px"                        (compact by default)
 */
const _OPENFREE_LIBERTY = "https://tiles.openfreemap.org/styles/liberty";
const _MAPLIBRE_DEMO    = "https://demotiles.maplibre.org/style.json";
const _GRAB_STYLE_URL   = (name) => `https://maps.grab.com/api/v1/styles/${name}/descriptor`;

function _grabStyle() {
  const key = process.env.REACT_APP_GRAB_MAPS_KEY;
  if (!key) return null;
  return {
    style: _GRAB_STYLE_URL("urban-light-partner"),
    transformRequest: (url) => url.startsWith("https://maps.grab.com/")
      ? { url, headers: { Authorization: key.startsWith("Bearer ") ? key : `Bearer ${key}` } }
      : { url },
  };
}

export default function PinDragMap({ initial, onCommit, label = "Pickup", height = "180px" }) {
  const wrapRef  = useRef(null);
  const mapRef   = useRef(null);
  const [address, setAddress] = useState(initial?.label || "");
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!wrapRef.current || mapRef.current) return;
    const start = initial && Number.isFinite(initial.lat) && Number.isFinite(initial.lng)
      ? [initial.lng, initial.lat]
      : [72.877, 19.076];   // default Mumbai
    const grab = _grabStyle();
    const map = new maplibregl.Map({
      container: wrapRef.current,
      style: grab?.style || _OPENFREE_LIBERTY,
      center: start,
      zoom: 15,
      attributionControl: false,
      transformRequest: grab?.transformRequest,
    });
    // Style fallback — if the primary tile source 500s, drop to demotiles.
    map.on("error", (e) => {
      if (String(e?.error?.message || "").includes("tiles")) {
        try { map.setStyle(_MAPLIBRE_DEMO); } catch { /* ignore */ }
      }
    });
    // Reverse-geocode as the user pans. We wait until `moveend` — mid-pan
    // reverse-geocode floods the API and jitters the UI.
    map.on("moveend", () => {
      const c = map.getCenter();
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        setLoading(true);
        try {
          const r = await api.get("/discover/reverse-geocode", { params: { lat: c.lat, lng: c.lng } });
          const label = (r.data && (r.data.display_name || r.data.name || r.data.address)) || `${c.lat.toFixed(5)}, ${c.lng.toFixed(5)}`;
          setAddress(label);
          onCommit?.({ lat: c.lat, lng: c.lng, label });
        } catch {
          const label = `${c.lat.toFixed(5)}, ${c.lng.toFixed(5)}`;
          setAddress(label);
          onCommit?.({ lat: c.lat, lng: c.lng, label });
        } finally {
          setLoading(false);
        }
      }, 350);
    });
    mapRef.current = map;
    return () => { try { map.remove(); } catch { /* ignore */ } mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the parent updates `initial` (e.g. after a saved-place tap),
  // re-centre the map so the pin follows the choice.
  useEffect(() => {
    if (!mapRef.current || !initial) return;
    if (Number.isFinite(initial.lat) && Number.isFinite(initial.lng)) {
      mapRef.current.easeTo({ center: [initial.lng, initial.lat], zoom: 15, duration: 350 });
      if (initial.label) setAddress(initial.label);
    }
  }, [initial?.lat, initial?.lng, initial?.label]);

  return (
    <div className="rounded-xl overflow-hidden border border-stone-200 relative" data-testid={`pindrag-${label.toLowerCase()}`}>
      <div className="absolute top-2 left-2 z-10 flex items-center gap-1 bg-white/95 backdrop-blur rounded-full px-2 py-0.5 text-[10px] font-bold text-stone-700 uppercase tracking-widest shadow-sm">
        <MapPin size={10} className="text-emerald-700" /> Drag map to set {label}
      </div>
      <div ref={wrapRef} style={{ height, width: "100%" }} className="bg-stone-100" />
      {/* Fixed centre pin — rendered via CSS overlay so it stays put while the
          map pans underneath (Grab / Uber pattern). */}
      <div className="pointer-events-none absolute inset-0 grid place-items-center">
        <div className="flex flex-col items-center -translate-y-2.5" data-testid={`pindrag-pin-${label.toLowerCase()}`}>
          <div className="text-[20px] leading-none drop-shadow-md">📍</div>
          <div className="w-1.5 h-1.5 rounded-full bg-black/30 shadow" />
        </div>
      </div>
      <div className="px-2 py-1.5 bg-white text-[11px] text-stone-800 flex items-center gap-1 min-h-[26px]">
        {loading
          ? <><Loader2 size={11} className="animate-spin text-stone-400" /> resolving address…</>
          : <span className="truncate" data-testid={`pindrag-address-${label.toLowerCase()}`}>{address || "Move the map to set the address"}</span>}
      </div>
    </div>
  );
}
